import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { AppMode } from '@/components/onboarding/onboardingSteps';

interface OnboardingState {
  toursSeen: Record<string, boolean>;
  completedTasks: Record<string, string[]>;
  dismissed: Record<string, boolean>;
}

const DEFAULT_STATE: OnboardingState = {
  toursSeen: {},
  completedTasks: {},
  dismissed: {},
};

function getStorageKey(userId: string) {
  return `munilex_onboarding_${userId}`;
}

function loadState(userId: string): OnboardingState {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (raw) return JSON.parse(raw);
  } catch {}
  return { ...DEFAULT_STATE };
}

function saveState(userId: string, state: OnboardingState) {
  localStorage.setItem(getStorageKey(userId), JSON.stringify(state));
}

export function useOnboarding(mode: AppMode) {
  const { user, profile } = useAuth();
  const [state, setState] = useState<OnboardingState>(DEFAULT_STATE);
  const [showTour, setShowTour] = useState(false);

  // Excluded roles
  const isExcluded = profile?.role === 'admin' || profile?.role === 'super_admin' || profile?.is_betatester;

  useEffect(() => {
    if (!user) return;
    const loaded = loadState(user.id);
    setState(loaded);

    // Auto-launch tour on first visit for this mode
    if (!isExcluded && !loaded.toursSeen[mode]) {
      // Small delay to let the page render targets
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user, mode, isExcluded]);

  const persist = useCallback((newState: OnboardingState) => {
    if (!user) return;
    setState(newState);
    saveState(user.id, newState);
  }, [user]);

  const markTourSeen = useCallback(() => {
    setShowTour(false);
    const newState = { ...state, toursSeen: { ...state.toursSeen, [mode]: true } };
    persist(newState);
  }, [state, mode, persist]);

  const completeTask = useCallback((taskId: string) => {
    const currentTasks = state.completedTasks[mode] || [];
    if (currentTasks.includes(taskId)) return;
    const newState = {
      ...state,
      completedTasks: { ...state.completedTasks, [mode]: [...currentTasks, taskId] },
    };
    persist(newState);
  }, [state, mode, persist]);

  const dismissChecklist = useCallback(() => {
    const newState = { ...state, dismissed: { ...state.dismissed, [mode]: true } };
    persist(newState);
  }, [state, mode, persist]);

  const resetTour = useCallback(() => {
    const newState = { ...state, toursSeen: { ...state.toursSeen, [mode]: false } };
    persist(newState);
    setShowTour(true);
  }, [state, mode, persist]);

  const completedTasks = state.completedTasks[mode] || [];
  const isChecklistDismissed = state.dismissed[mode] || false;

  return {
    showTour,
    setShowTour,
    markTourSeen,
    completedTasks,
    completeTask,
    isChecklistDismissed,
    dismissChecklist,
    resetTour,
    isExcluded: !!isExcluded,
  };
}
